import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Lock, User, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_BLUE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/logo_transparent.png`;

const errorText = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg).filter(Boolean).join(' ');
  return fallback;
};

function IconInput({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <Input className="pl-10" {...props} />
    </div>
  );
}

function ChangePasswordForm({ currentPassword, tempToken, onDone }) {
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) { toast.error('Mindestens 10 Zeichen'); return; }
    if (pw !== confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/change-password`, { current_password: currentPassword, new_password: pw },
        { headers: { Authorization: `Bearer ${tempToken}` } });
      toast.success('Neues Passwort gespeichert');
      onDone(res.data);
    } catch (err) {
      toast.error(errorText(err, 'Passwort konnte nicht geändert werden'));
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-force-password-form">
      <h2 className="text-lg font-bold text-[#0F172A] mb-1">Neues Passwort festlegen</h2>
      <p className="text-sm text-slate-500 mb-6">Aus Sicherheitsgründen muss das Standard-Passwort jetzt durch ein eigenes ersetzt werden (mind. 10 Zeichen).</p>
      <div className="space-y-5">
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Neues Passwort</Label>
          <IconInput icon={KeyRound} type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" data-testid="admin-new-password-input" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Passwort wiederholen</Label>
          <IconInput icon={KeyRound} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" data-testid="admin-confirm-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-[#066ce7] hover:bg-[#005bb5] text-white py-3 font-semibold" data-testid="admin-save-new-password-btn">
          {loading ? 'Speichern…' : 'Passwort speichern & anmelden'}
        </Button>
      </div>
    </form>
  );
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const finish = (data) => {
    localStorage.setItem('itcs-admin-token', data.token);
    localStorage.setItem('itcs-admin-user', data.username);
    navigate('/admin');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.must_change_password) {
        setTempToken(res.data.token);
      } else {
        toast.success('Login erfolgreich');
        finish(res.data);
      }
    } catch (err) {
      toast.error(err.response?.status === 429 ? errorText(err, 'Zu viele Fehlversuche') : 'Ungültige Anmeldedaten');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_BLUE} alt="ITCS" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-slate-400 text-sm mt-2">ITCS Industrial Management</p>
        </div>
        {tempToken ? (
          <ChangePasswordForm currentPassword={password} tempToken={tempToken} onDone={finish} />
        ) : (
          <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login-form">
            <div className="space-y-5">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Username</Label>
                <IconInput icon={User} value={username} onChange={e => setUsername(e.target.value)} placeholder="admin" autoComplete="username" data-testid="admin-username-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Password</Label>
                <IconInput icon={Lock} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="********" autoComplete="current-password" data-testid="admin-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#066ce7] hover:bg-[#005bb5] text-white py-3 font-semibold" data-testid="admin-login-btn">
                {loading ? 'Loading...' : 'Anmelden'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
